// Asignación de bodegas por usuario (bloqueo duro de transacciones).
// Inventario → Bodegas → Permisos de bodega.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Save, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { Location, Switch, UsuarioLocation, UsuarioOpt, fieldCls, inv, locationsList, usuariosList } from './shared';

interface Fila { location_id: string; asignada: boolean; puede_recibir: boolean; puede_transferir: boolean; puede_ajustar: boolean }

export function PermisosBodega() {
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const esAdmin = isAdmin();
  const [usuarioId, setUsuarioId] = useState('');

  const { data: usuarios = [] } = useQuery<UsuarioOpt[]>({ queryKey: ['inv-usuarios'], queryFn: usuariosList });
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: asignadas = [], isFetching } = useQuery<UsuarioLocation[]>({
    queryKey: ['inv-usuloc', usuarioId], enabled: !!usuarioId,
    queryFn: () => inv.list<UsuarioLocation>('usuario_locations', `?select=*&usuario_id=eq.${usuarioId}`),
  });

  const [filas, setFilas] = useState<Record<string, Fila>>({});
  useEffect(() => {
    const map: Record<string, Fila> = {};
    for (const b of bodegas) {
      const a = asignadas.find((x) => x.location_id === b.id && x.es_activo);
      map[b.id] = {
        location_id: b.id,
        asignada: !!a,
        puede_recibir: a?.puede_recibir ?? true,
        puede_transferir: a?.puede_transferir ?? true,
        puede_ajustar: a?.puede_ajustar ?? false,
      };
    }
    setFilas(map);
  }, [bodegas, asignadas, usuarioId]);

  const setFila = (id: string, patch: Partial<Fila>) =>
    setFilas((f) => ({ ...f, [id]: { ...f[id], ...patch } }));

  // Encender cualquier permiso (recibir/transferir/ajustar) habilita la bodega
  // automáticamente: si no, el Guardar ignoraba la fila y el toggle verde mentía.
  const setPerm = (id: string, patch: Partial<Fila>) =>
    setFilas((f) => {
      const merged = { ...f[id], ...patch };
      if (Object.values(patch).some((v) => v === true)) merged.asignada = true;
      return { ...f, [id]: merged };
    });

  const guardar = useMutation({
    mutationFn: async () => {
      for (const b of bodegas) {
        const f = filas[b.id];
        const a = asignadas.find((x) => x.location_id === b.id);
        if (f.asignada) {
          const body = { usuario_id: usuarioId, location_id: b.id, puede_recibir: f.puede_recibir, puede_transferir: f.puede_transferir, puede_ajustar: f.puede_ajustar, es_activo: true, deleted_at: null };
          if (a) await inv.update('usuario_locations', a.id, body);
          else await inv.create('usuario_locations', body);
        } else if (a) {
          await inv.update('usuario_locations', a.id, { es_activo: false });
        }
      }
    },
    onSuccess: () => { toast.success('Permisos actualizados'); void qc.invalidateQueries({ queryKey: ['inv-usuloc', usuarioId] }); },
    onError: (e) => {
      const msg = getErrorMessage(e);
      if (/row.?level security|row security|violate|42501/i.test(msg))
        toast.error('Necesitas ser administrador (nivel ≥ 5) para cambiar permisos de bodega.');
      else toast.error(msg);
    },
  });

  const totalAsignadas = useMemo(() => Object.values(filas).filter((f) => f.asignada).length, [filas]);

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Permisos de bodega por usuario</h1>
      {!esAdmin && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Solo un <strong>administrador (nivel ≥ 5)</strong> puede cambiar permisos de bodega. Puedes consultar la configuración, pero no guardarla.</span>
        </div>
      )}
      <Card>
        <CardContent className="pt-4">
          <label className="block max-w-md space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Usuario</span>
            <select className={fieldCls} value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)}>
              <option value="">— Selecciona un usuario —</option>
              {usuarios.map((u) => <option key={u.id} value={u.id}>{u.username}{u.email ? ` (${u.email})` : ''}</option>)}
            </select>
          </label>
        </CardContent>
      </Card>

      {usuarioId && (
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Bodegas habilitadas ({totalAsignadas}/{bodegas.length})</CardTitle>
            <Button isLoading={guardar.isPending} disabled={!esAdmin} title={esAdmin ? '' : 'Requiere administrador (nivel ≥ 5)'} onClick={() => guardar.mutate()}><Save className="w-4 h-4 mr-1" /> Guardar</Button>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Bodega</th><th className="py-2 pr-3">Habilitada</th>
                <th className="py-2 pr-3">Recibir</th><th className="py-2 pr-3">Transferir</th><th className="py-2 pr-3">Ajustar</th>
              </tr></thead>
              <tbody>
                {bodegas.map((b) => {
                  const f = filas[b.id]; if (!f) return null;
                  return (
                    <tr key={b.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 pr-3">{b.nombre} {b.es_principal && <span className="text-xs text-muted-foreground">(principal)</span>}</td>
                      <td className="py-2 pr-3"><Switch checked={f.asignada} disabled={!esAdmin} onChange={(v) => setFila(b.id, { asignada: v })} label="" /></td>
                      <td className="py-2 pr-3"><Switch checked={f.asignada && f.puede_recibir} disabled={!esAdmin} onChange={(v) => setPerm(b.id, { puede_recibir: v })} label="" /></td>
                      <td className="py-2 pr-3"><Switch checked={f.asignada && f.puede_transferir} disabled={!esAdmin} onChange={(v) => setPerm(b.id, { puede_transferir: v })} label="" /></td>
                      <td className="py-2 pr-3"><Switch checked={f.asignada && f.puede_ajustar} disabled={!esAdmin} onChange={(v) => setPerm(b.id, { puede_ajustar: v })} label="" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-muted-foreground">Un permiso (Recibir/Transferir/Ajustar) solo aplica si la bodega está <strong>Habilitada</strong>; al encender un permiso la bodega se habilita sola. Recuerda pulsar <strong>Guardar</strong>. Los administradores (perfil nivel ≥ 5) operan todas las bodegas sin necesidad de asignación.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
